import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';

var ModalPortal = function ModalPortal(_ref) {
  var id = _ref.id,
      children = _ref.children;
  var domNode = useRef(getOrCreateElementById(id));
  useEffect(function () {
    return function () {
      // remove the div on unmount
      var current = domNode.current;
      if (current) document.body.removeChild(current);
    };
  }, [id]);
  if (domNode.current) return ReactDOM.createPortal(children, domNode.current);
  return null;
};

function getOrCreateElementById(id) {
  if (typeof window === "undefined") {
    return null;
  }

  var domNode = document.getElementById(id);

  if (domNode) {
    return domNode;
  } else {
    var element = document.createElement('div');
    element.id = id;
    document.body.appendChild(element);
    return element;
  }
}

var Overlay = function Overlay(_ref) {
  var onClick = _ref.onClick,
      children = _ref.children;
  return React.createElement("div", {
    onClick: onClick,
    style: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.75)'
    }
  }, children);
};

var Modal =
/*#__PURE__*/
React.forwardRef(function (_ref, ref) {
  var role = _ref.role,
      children = _ref.children;
  return React.createElement("div", {
    role: role,
    ref: ref,
    tabIndex: -1,
    style: {
      position: 'absolute',
      top: '40px',
      left: '40px',
      right: '40px',
      bottom: '40px',
      border: '1px solid #ccc',
      background: '#fff',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      borderRadius: '4px',
      outline: 'none',
      padding: '20px'
    }
  }, children);
});

function useBodyScrollLock() {
  useLayoutEffect(function () {
    // Get original body overflow
    var originalStyle = window.getComputedStyle(document.body).overflow; // Prevent scrolling on mount

    document.body.style.overflow = 'hidden'; // Re-enable scrolling when component unmounts

    return function () {
      document.body.style.overflow = originalStyle;
    };
  }, []); // Empty array ensures effect is only run on mount and unmount
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

/*!
 * Adapted from jQuery UI core
 *
 * http://jqueryui.com
 *
 * Copyright 2014 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/category/ui-core/
 */
var tabbableNode = /input|select|textarea|button|object/;

function hidesContents(element) {
  var zeroSize = element.offsetWidth <= 0 && element.offsetHeight <= 0; // If the node is empty, this is good enough

  if (zeroSize && !element.innerHTML) return true; // Otherwise we need to check some styles

  var style = window.getComputedStyle(element);
  return zeroSize ? style.getPropertyValue('overflow') !== 'visible' : style.getPropertyValue('display') === 'none';
}

function visible(element) {
  var parentElement = element;

  while (parentElement) {
    if (parentElement === document.body) break;
    if (hidesContents(parentElement)) return false;
    parentElement = parentElement.parentNode;
  }

  return true;
}

function focusable(element, isTabIndexNotNaN) {
  var nodeName = element.nodeName.toLowerCase();
  var res = //@ts-ignore
  tabbableNode.test(nodeName) && !element.disabled || ( //@ts-ignore
  nodeName === 'a' ? element.href || isTabIndexNotNaN : isTabIndexNotNaN);
  return res && visible(element);
}
function tabbable(element) {
  var tabIndex = element.getAttribute('tabindex'); //@ts-ignore

  if (tabIndex === null) tabIndex = undefined; //@ts-ignore

  var isTabIndexNaN = isNaN(tabIndex); //@ts-ignore

  return (isTabIndexNaN || tabIndex >= 0) && focusable(element, !isTabIndexNaN);
}
function findTabbableDescendants(element) {
  return [].slice.call(element.querySelectorAll('*'), 0).filter(tabbable);
}

function useHandleKeyPress(callback) {
  useEffect(function () {
    window.addEventListener('keydown', callback);
    return function () {
      window.removeEventListener('keydown', callback);
    };
  }, [callback]);
}

var TAB_KEY = 9;
var optionsDefault = {
  focusOnRender: true,
  returnFocus: true
};
function useTrapFocus(opts) {
  var options = opts ? _extends({}, optionsDefault, {}, opts) : optionsDefault;
  var ref = useRef(null);
  var previouseFocusedElement = useRef(document.activeElement);

  var _useState = useState([]),
      tabbableElements = _useState[0],
      setTabbableElements = _useState[1]; // Handle initial focus of the referenced element, and return focus to previously focused element on cleanup
  // and find all the tabbable elements in the referenced element


  useEffect(function () {
    var current = ref.current;

    if (current) {
      var focusableChildNodes = findTabbableDescendants(current);

      if (options.focusOnRender) {
        current.focus();
      }

      setTabbableElements(focusableChildNodes);
    }

    return function () {
      var current = previouseFocusedElement.current;

      if (current instanceof HTMLElement && options.returnFocus) {
        current.focus();
      }
    };
  }, [options.focusOnRender, options.returnFocus, ref, setTabbableElements]);
  var handleUserKeyPress = useCallback(function (event) {
    var keyCode = event.keyCode,
        shiftKey = event.shiftKey;
    var first = tabbableElements[0];
    var last = tabbableElements[tabbableElements.length - 1];
    var currentActiveElement = document.activeElement; // Scope current tabs to current root element

    if (isWithinCurrentElementScope([].concat(tabbableElements, [ref.current]))) {
      if (keyCode === TAB_KEY) {
        if (currentActiveElement === first || currentActiveElement === ref.current) {
          // move focus to last element if shift+tab while currently focusing the first tabbable element
          if (shiftKey) {
            event.preventDefault();
            last.focus();
          }
        }

        if (currentActiveElement === last) {
          // move focus back to first if tabbing while currently focusing the last tabbable element
          if (!shiftKey) {
            event.preventDefault();
            first.focus();
          }
        }
      }
    }
  }, [ref, tabbableElements]);
  useHandleKeyPress(handleUserKeyPress);
  return ref;
}

function isWithinCurrentElementScope(elementList) {
  var currentActiveElement = document.activeElement;
  return elementList.includes(currentActiveElement);
}

var ESC = 27;
var Modal$1 = function Modal$1(_ref) {
  var _ref$id = _ref.id,
      id = _ref$id === void 0 ? 'modal-root' : _ref$id,
      _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
      _ref$role = _ref.role,
      role = _ref$role === void 0 ? 'dialog' : _ref$role,
      _ref$shouldFocusAfter = _ref.shouldFocusAfterRender,
      shouldFocusAfterRender = _ref$shouldFocusAfter === void 0 ? true : _ref$shouldFocusAfter,
      onOverlayClick = _ref.onOverlayClick,
      _ref$shouldReturnFocu = _ref.shouldReturnFocusAfterClose,
      shouldReturnFocusAfterClose = _ref$shouldReturnFocu === void 0 ? true : _ref$shouldReturnFocu,
      onEscape = _ref.onEscape,
      children = _ref.children;
  //TODO: use useCloseOnEsc
  var handleOnEsc = useCallback(function (event) {
    var keyCode = event.keyCode;

    if (isOpen && keyCode === ESC) {
      onEscape();
    }
  }, [onEscape, isOpen]);
  useHandleKeyPress(handleOnEsc);
  useBodyScrollLock();
  var modalRef = useTrapFocus({
    focusOnRender: shouldFocusAfterRender,
    returnFocus: shouldReturnFocusAfterClose
  });
  return isOpen ? React.createElement(ModalPortal, {
    id: id
  }, React.createElement(Overlay, {
    onClick: onOverlayClick
  }, React.createElement(Modal, {
    role: role,
    ref: modalRef
  }, children))) : null;
};

/**
 * set aria-hidden attribute on element with id
 * @param {string} appElementId
 */

function useAriaHide(appElementId) {
  var appElementRef = useRef(document.getElementById(appElementId));
  useEffect(function () {
    var appElement = appElementRef.current;

    if (appElement) {
      appElement.setAttribute('aria-hidden', 'true');
    }

    return function () {
      if (appElement) {
        appElement.removeAttribute('aria-hidden');
      }
    };
  }, []);
}

var ESC$1 = 27;
function useCloseOnEsc(onEscape) {
  var handleOnEsc = useCallback(function (event) {
    var keyCode = event.keyCode;

    if (keyCode === ESC$1) {
      onEscape();
    }
  }, [onEscape]);
  useHandleKeyPress(handleOnEsc);
}

export { Modal$1 as Modal, ModalPortal, useAriaHide, useBodyScrollLock, useCloseOnEsc, useHandleKeyPress, useTrapFocus };
//# sourceMappingURL=react-customizable-modal.esm.js.map
